import type { MiceEventPremises } from '@voll-intelligence/domain';

export interface DestinationForm {
  /** A identidade da linha no formulário (a `key` do React); não vai para as premissas. */
  key: string;
  iata: string;
  arrivalIdeal: string;
  arrivalTolerated: string;
  lastResort: boolean;
}
export interface PremisesForm {
  destinations: DestinationForm[];
  redEyeStart: string;
  redEyeEnd: string;
  redEyeArrivalUntil: string;
  preferredDepartureFrom: string;
  returnDate: string;
  returnEarliestIdeal: string;
  returnEarliestTolerated: string;
  checkedBaggage: boolean;
  arrivalDayBefore: boolean;
  savingsThreshold: string;
  notes: string;
  /** Sem campos no formulário: carregado das premissas para o PUT não zerar o que o rascunho trouxe. */
  rooming: MiceEventPremises['rooming'];
  transfer: MiceEventPremises['transfer'];
}

let lastDestinationKey = 0;
const destinationKey = (): string => {
  lastDestinationKey += 1;
  return `destination-${String(lastDestinationKey)}`;
};

/** O que o formulário só carrega (sem campo): a hospedagem e o transfer. */
const carriedOf = (premises: MiceEventPremises | null) => ({
  rooming: premises?.rooming ?? null,
  transfer: premises?.transfer ?? null,
});

export const emptyDestination = (): DestinationForm => ({
  key: destinationKey(),
  iata: '',
  arrivalIdeal: '',
  arrivalTolerated: '',
  lastResort: false,
});

const toDestinationForm = (d: MiceEventPremises['destinations'][number]): DestinationForm => ({
  key: destinationKey(),
  iata: d.iata,
  arrivalIdeal: d.arrivalIdeal ?? '',
  arrivalTolerated: d.arrivalTolerated ?? '',
  lastResort: d.lastResort,
});

type ReturnForm = Pick<
  PremisesForm,
  'returnDate' | 'returnEarliestIdeal' | 'returnEarliestTolerated'
>;

const toReturnForm = (premises: MiceEventPremises | null): ReturnForm => {
  const r = premises?.returnDeparture;
  return {
    returnDate: r?.date ?? '',
    returnEarliestIdeal: r?.earliestIdeal ?? '',
    returnEarliestTolerated: r?.earliestTolerated ?? '',
  };
};

type RedEyeForm = Pick<PremisesForm, 'redEyeStart' | 'redEyeEnd' | 'redEyeArrivalUntil'>;

const toRedEyeForm = (premises: MiceEventPremises | null): RedEyeForm => ({
  redEyeStart: premises?.redEye.start ?? '00:00',
  redEyeEnd: premises?.redEye.end ?? '06:00',
  redEyeArrivalUntil: premises?.redEyeArrivalUntil ?? '',
});

export const toForm = (premises: MiceEventPremises | null): PremisesForm => ({
  destinations: premises ? premises.destinations.map(toDestinationForm) : [emptyDestination()],
  ...toRedEyeForm(premises),
  preferredDepartureFrom: premises?.preferredDepartureFrom ?? '',
  ...toReturnForm(premises),
  checkedBaggage: premises?.checkedBaggage ?? false,
  arrivalDayBefore: premises?.arrivalDayBefore ?? false,
  savingsThreshold: premises?.savingsReviewThreshold
    ? String(premises.savingsReviewThreshold.amount)
    : '',
  notes: premises?.notes ?? '',
  ...carriedOf(premises),
});

const orNull = (v: string): string | null => (v === '' ? null : v);

/** Sem nenhum campo da volta, sem janela; dia ou tolerado sem o ideal não passa no schema. O
 *  tolerado é sempre o mais cedo: digitado depois do ideal, os dois trocam de lugar. */
const toReturnDeparture = (f: PremisesForm): unknown => {
  const { returnDate, returnEarliestIdeal: ideal, returnEarliestTolerated: tolerated } = f;
  if (returnDate === '' && ideal === '' && tolerated === '') return null;
  const swap = ideal !== '' && tolerated > ideal;
  return {
    date: orNull(returnDate),
    earliestIdeal: swap ? tolerated : ideal,
    earliestTolerated: orNull(swap ? ideal : tolerated),
  };
};

export const toPremises = (f: PremisesForm): unknown => ({
  destinations: f.destinations.map((d) => ({
    iata: d.iata.trim().toUpperCase(),
    arrivalIdeal: orNull(d.arrivalIdeal),
    arrivalTolerated: orNull(d.arrivalTolerated),
    lastResort: d.lastResort,
  })),
  redEye: { start: f.redEyeStart, end: f.redEyeEnd },
  redEyeArrivalUntil: orNull(f.redEyeArrivalUntil),
  preferredDepartureFrom: orNull(f.preferredDepartureFrom),
  returnDeparture: toReturnDeparture(f),
  checkedBaggage: f.checkedBaggage,
  arrivalDayBefore: f.arrivalDayBefore,
  savingsReviewThreshold:
    f.savingsThreshold === '' ? null : { amount: Number(f.savingsThreshold), currency: 'BRL' },
  notes: f.notes,
  rooming: f.rooming,
  transfer: f.transfer,
});
